import React, { useState, useEffect, useMemo } from "react";
import { FONT_ROBOTO_MONO } from "../../../utils/fonts";
import { Spinner } from "reactstrap";
import Select from "react-select";
import { stylesSelect, DropdownIndicator, ClearIndicator } from "../../../Store/styled/select";
import { useDispatch, useSelector } from "react-redux";
import {
  getTrialBalance,
  setTrialBalanceReportLoading,
} from "../../../Store/Reducer/report.slice";
import { Formik, Form } from "formik";
import {
  getSegmentOptionList,
  setAgentORSubBrokerList,
} from "../../../Store/Reducer/common.slice";
import { getAuthoritysList } from "../../../Store/Reducer/adminSlice/admin.slice";
import { setValansList } from "../../../Store/Reducer/valan.slice";
import DatePicker from "react-datepicker";
import { getSessionData } from "utils/common";
import { UI_DATEPICKER_FORMAT } from "utils/dateFormats";
import useMobileViewport from "hooks/useMobileViewport";
import MobileRecordCard from "components/mobile/MobileRecordCard";
import MobileRecordList from "components/mobile/MobileRecordList";

const orderByOption = [
  { label: "Client Name", value: 1 },
  { label: "Client Code", value: 2 },
];

// ─── Table ───────────────────────────────────────────────────────────────────

function TrialBalanceTable({ data, loading }) {
  const isMobileViewport = useMobileViewport();
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ?  1 : -1;
      return 0;
    });
  }, [data, sortKey, sortAsc]);

  const debitTotal  = useMemo(() => data.filter(r => r.crDr === "Dr").reduce((s, r) => s + (r.balance || 0), 0), [data]);
  const creditTotal = useMemo(() => data.filter(r => r.crDr === "Cr").reduce((s, r) => s + (r.balance || 0), 0), [data]);

  const SortArrow = ({ col }) => (
    <span style={{ marginLeft: 3, fontSize: 9, opacity: .7 }}>
      {sortKey === col ? (sortAsc ? "▲" : "▼") : "⇅"}
    </span>
  );

  if (isMobileViewport) {
    return (
      <div className="rpt-table-panel rpt-table-panel--mobile-cards fx-module--mobile-cards">
        <MobileRecordList
          loading={loading && sorted.length === 0}
          loadingText="Loading ..."
          empty={!loading && sorted.length === 0}
          emptyText="No data found"
        >
          {sorted.map((row, idx) => (
            <MobileRecordCard
              key={idx}
              title={row.userName}
              subtitle={row.userCode}
              badge={row.crDr}
              badgeVariant={row.crDr === "Dr" ? "sell" : "buy"}
              fields={[
                {
                  label: "Debit",
                  value: row.crDr === "Dr" ? (row.balance || 0).toFixed(2) : "—",
                  tone: "red",
                },
                {
                  label: "Credit",
                  value: row.crDr === "Cr" ? (row.balance || 0).toFixed(2) : "—",
                  tone: "blue",
                },
              ]}
            />
          ))}
          {data.length > 0 && (
            <MobileRecordCard
              title="Total"
              fields={[
                { label: "Debit", value: debitTotal.toFixed(2), tone: "red" },
                { label: "Credit", value: creditTotal.toFixed(2), tone: "blue" },
              ]}
            />
          )}
        </MobileRecordList>
      </div>
    );
  }

  return (
    <div className="rpt-table-panel">
      <div className="rpt-table-scroll">
        <table className="rpt-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("userCode")}>
                Client Code <SortArrow col="userCode" />
              </th>
              <th className="sortable" onClick={() => handleSort("userName")}>
                Particulars <SortArrow col="userName" />
              </th>
              <th className="text-right" style={{ color: "var(--red)" }}>Debit</th>
              <th className="text-right" style={{ color: "var(--blue)" }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, idx) => (
              <tr key={idx}>
                <td style={{ fontFamily: FONT_ROBOTO_MONO }}>{row.userCode}</td>
                <td>{row.userName}</td>
                <td className="text-right col-red">
                  {row.crDr === "Dr" ? (row.balance || 0).toFixed(2) : "—"}
                </td>
                <td className="text-right col-blue">
                  {row.crDr === "Cr" ? (row.balance || 0).toFixed(2) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && (
            <tfoot>
              <tr>
                <td><b>Total</b></td>
                <td />
                <td className="text-right col-red">{debitTotal.toFixed(2)}</td>
                <td className="text-right col-blue">{creditTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {loading && <div className="rpt-loading">Loading ...</div>}
        {!loading && data.length === 0 && <div className="rpt-empty">No data found</div>}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function TrialBalance() {
  const dispatch = useDispatch();
  const { trialBalanceReportLoading, trialBalanceList } = useSelector(({ report }) => report);
  const { userId } = getSessionData();

  const initialState = {
    dateFilter: "1", startDate: new Date(), endDate: new Date(),
    orderBy: orderByOption[1], filterBy: "", withZero: false,
  };
  const [initialForm, setInitialForm] = useState(initialState);

  useEffect(() => {
    dispatch(getSegmentOptionList());
    dispatch(getAuthoritysList());
    setInitialForm({ ...initialState });
    return () => {
      dispatch(setAgentORSubBrokerList([]));
      dispatch(setValansList([]));
      dispatch(setTrialBalanceReportLoading(false));
    };
  }, []);

  useEffect(() => {
    dispatch(getTrialBalance({ userId, dateFilter: "1", orderBy: orderByOption[1] }));
  }, [dispatch]);

  return (
    <div className="rpt-page">
      {trialBalanceReportLoading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.3)", zIndex: 999 }}>
          <Spinner color="primary" />
        </div>
      )}

      {/* Title + export buttons */}
      <div className="rpt-title-bar">
        <div className="rpt-title">Trial Balance</div>
        <div className="rpt-title-actions">
          <button className="rpt-btn rpt-btn--pdf"
            onClick={() => dispatch(getTrialBalance({ userId, isSubmit: "PDF" }))}>
            <span className="material-icons-outlined">picture_as_pdf</span>
            PDF Report
          </button>
          <button className="rpt-btn rpt-btn--excel"
            onClick={() => dispatch(getTrialBalance({ userId, isSubmit: "EXCEL" }))}>
            <span className="material-icons-outlined">grid_on</span>
            Excel Report
          </button>
        </div>
      </div>
      <hr className="rpt-divider" />

      {/* Filter */}
      <Formik
        enableReinitialize
        initialValues={initialForm}
        onSubmit={(values) => dispatch(getTrialBalance({ ...values, userId }))}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <div className="rpt-filter">
              <div className="rpt-filter-row">
                {/* Date filter */}
                <div>
                  <div className="rpt-filter-label">Date Filter</div>
                  <div className="rpt-radio-group">
                    {[{ val: "1", label: "Current Date" }, { val: "2", label: "Custom Date" }].map(({ val, label }) => (
                      <label key={val} className={`rpt-radio ${values.dateFilter === val ? "active" : ""}`}>
                        <input type="radio" name="dateFilter" value={val} checked={values.dateFilter === val}
                          onChange={(e) => handleChange("dateFilter")(e.target.value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Custom dates */}
                {values.dateFilter === "2" && (
                  <>
                    <div>
                      <div className="rpt-filter-label">From</div>
                      <DatePicker className="form-control" dateFormat={UI_DATEPICKER_FORMAT} selected={values.startDate}
                        onChange={(d) => setFieldValue("startDate", d)} />
                    </div>
                    <div>
                      <div className="rpt-filter-label">To</div>
                      <DatePicker className="form-control" dateFormat={UI_DATEPICKER_FORMAT} selected={values.endDate}
                        onChange={(d) => setFieldValue("endDate", d)} />
                    </div>
                  </>
                )}

                {/* Order by */}
                <div style={{ minWidth: 170 }}>
                  <div className="rpt-filter-label">Order By</div>
                  <Select styles={stylesSelect} components={{ DropdownIndicator, ClearIndicator }}
                    options={orderByOption} name="orderBy" value={values.orderBy}
                    onChange={(opt) => handleChange({ target: { name: "orderBy", value: opt } })}
                  />
                </div>

                {/* With zero */}
                <div style={{ alignSelf: "flex-end" }}>
                  <label className="rpt-checkbox">
                    <input type="checkbox" checked={values.withZero}
                      onChange={(e) => setFieldValue("withZero", e.target.checked)} />
                    With Zero Balance
                  </label>
                </div>

                <div style={{ alignSelf: "flex-end" }}>
                  <button type="submit" className="rpt-btn rpt-btn--primary">
                    <i className="material-icons-outlined">analytics</i> Get Report
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {/* Summary chips */}
      {trialBalanceList?.length > 0 && (
        <div className="rpt-summary-chips" style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ background: "rgba(239,83,80,.1)", border: "1px solid var(--red)", borderRadius: 5, padding: "6px 14px", fontSize: 12, color: "var(--red)" }}>
            <span style={{ color: "var(--text-muted)", fontSize: 10, display: "block", marginBottom: 2 }}>TOTAL DEBIT</span>
            <b>{trialBalanceList.filter(r => r.crDr === "Dr").reduce((s, r) => s + (r.balance || 0), 0).toFixed(2)}</b>
          </div>
          <div style={{ background: "rgba(38,166,154,.1)", border: "1px solid var(--green)", borderRadius: 5, padding: "6px 14px", fontSize: 12, color: "var(--green)" }}>
            <span style={{ color: "var(--text-muted)", fontSize: 10, display: "block", marginBottom: 2 }}>TOTAL CREDIT</span>
            <b>{trialBalanceList.filter(r => r.crDr === "Cr").reduce((s, r) => s + (r.balance || 0), 0).toFixed(2)}</b>
          </div>
          <div style={{ background: "rgba(85,153,238,.1)", border: "1px solid var(--blue)", borderRadius: 5, padding: "6px 14px", fontSize: 12, color: "var(--blue)" }}>
            <span style={{ color: "var(--text-muted)", fontSize: 10, display: "block", marginBottom: 2 }}>TOTAL ACCOUNTS</span>
            <b>{trialBalanceList.length}</b>
          </div>
        </div>
      )}

      <TrialBalanceTable data={trialBalanceList || []} loading={trialBalanceReportLoading} />
    </div>
  );
}

export default TrialBalance;
